import moment from 'moment';
import React, { Fragment } from 'react';
import { IError } from '../api';
import { Rate } from '../enums';
import { Currency, Language, PhoneInputConfig } from '../interfaces/config';
import { __t } from './t';
import { RegistrationCountry } from '../interfaces/api';

export const isEmptyObject = (obj: object): boolean =>
    obj && Object.keys(obj).length === 0 && Object.getPrototypeOf(obj) === Object.prototype;

export const pluralize = (count: number, words: string[]): string => {
    const cases = [2, 0, 1, 1, 1, 2];
    return words[count % 100 > 4 && count % 100 < 20 ? 2 : cases[Math.min(count % 10, 5)]];
};

export const timestampWithNoMillisecondsToDate = (timestamp: number): Date => new Date(Number(timestamp + '000'));

export const dateTotimestampWithNoMilliseconds = (date: Date): number => Number(moment(date).format('X'));

export const highlightMatchingText = (text: string, matchingText: string) => {
    const matchRegex = RegExp(matchingText, 'ig');

    const matches = Array.from(text.matchAll(matchRegex));

    return text.split(matchRegex).map((nonBoldText, index, arr) => {
        return (
            <Fragment key={index}>
                {nonBoldText}
                {index + 1 !== arr.length && <strong>{matches[index]}</strong>}
            </Fragment>
        );
    });
};

export interface RefMap {
    id: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ref: any;
}

export const getRefById = (id: string, refMap: RefMap[]) => refMap.find((i) => i.id === id);

export const scrollToError = (errors: IError, refMap: RefMap[]): void => {
    if (errors && Object.keys(errors).length) {
        for (const item of refMap) {
            if (errors[item.id]?.length && item.ref.current) {
                item.ref.current.scrollIntoView({ behavior: 'smooth' }, true);
                break;
            }
        }
    }
};

export const getAreaIconById = (id: number): string => {
    switch (id) {
        case 1:
            return 'trowel-bricks-light';
        case 2:
            return 'truck-moving-light';
        case 3:
            return 'hotel-light';
        case 4:
            return 'conveyor-belt-boxes-light';
        case 5:
            return 'spa-light';
        case 6:
            return 'tractor-light';
        case 7:
            return 'roller-coaster-light';
        case 8:
            return 'cow-light';
        case 9:
            return 'warehouse-full-light';
        case 10:
            return 'utility-pole-double-light';
        case 11:
            return 'store-light';
        case 12:
            return 'chair-office-light';
        case 13:
            return 'car-wrench-light';
        case 14:
            return 'industry-windows-light';
        case 15:
            return 'shirt-long-sleeve-light';
        case 16:
            return 'axe-light';
        case 17:
            return 'display-code-light';
        case 18:
            return 'person-digging-light';
        case 19:
            return 'building-columns-light';
        case 20:
            return 'syringe-light';
        case 21:
            return 'books-light';
        case 22:
            return 'fill-drip-light';
        case 23:
            return 'compass-drafting-light';

        default:
            return 'user-helmet-safety-light';
    }
};

export const preparePhoneInputConfig = (registrationCountries: RegistrationCountry[]): PhoneInputConfig => {
    const countries = registrationCountries.map((item) => item.code);
    const preferredCountries = registrationCountries.filter((item) => item.isPopular).map((item) => item.code);
    const localization: { [key: string]: string } = {};
    registrationCountries.map((item) => (localization[item.code] = item.name));

    return {
        countries,
        preferredCountries,
        localization,
    };
};

export const getFilePreviewUrl = (file: File): string => URL.createObjectURL(file);

export const getCurrencyById = (currencies: Currency[], currencyId: number): Currency | undefined =>
    currencies.find((c) => c.id === currencyId);

export const getLanguageById = (languages: Language[], languageId: number): string =>
    languages.find((l) => l.id === languageId)?.name || '';

export const mapRateTypeToString = (rateType: Rate): string => {
    switch (rateType) {
        case Rate.Hourly:
            return __t('ч');
        case Rate.Monthly:
            return __t('м');
        default:
            console.error(`Unsupported rate type ${rateType} provided!`);
            return '';
    }
};

export const cutAgeToFirstLetter = (ageFullString: string): string => {
    const [age, text] = ageFullString.split(' ');
    return `${age} ${text.slice(0, 1)}.`;
};

export const removeAllHTMLTags = (str: string): string => {
    return str.replace(/(<([^>]+)>)/gi, '');
};
